// The colgroup: the single home of the table's horizontal geometry. Every rendered column gets one <col> in
// header order; a data column's width is its saved width, else its configured initial width, raised to its
// content floor. While the table is narrower than its region, the spare width goes first to columns whose
// header or value shows truncated (spareWidths, view.js). The table lays out from these widths (table-layout:
// fixed) at their exact sum. One column fills what remains (fillColumn): the last resizable, unlocked text/link
// column, else the last resizable, unlocked data column of any type; a fixed-width column (min equals max) never
// fills. The fill may grow past its maximum, so a full-page table always reaches its region's edge.

import React from 'react'

import { useTable } from '../context'
import { DEFAULT_WIDTHS, DEFAULT_DENSITY } from '../config/defaults'
import {
  isResizable,
  renderedWidth,
  resizedWidths,
  spareWidths,
} from '../view/view'
import { controlWidth } from './controlSize'

const isText = value => typeof value === 'string' && value.trim() !== ''

/** A resizable column's content floor (contentFloor.js), when one is measured. */
export const floorOf = (table, column) => {
  const floors = table.geometry && table.geometry.floors
  return isResizable(column) && floors ? floors[column.key] : undefined
}

/** The width a data column renders at before spare width: its saved or initial width, raised to its floor. */
export const columnWidth = (table, column) => {
  const view = table.state.view || {}
  const floor = floorOf(table, column)
  const saved = view.widths || {}
  const natural = ((table.geometry || {}).naturals || {})[column.key] || 0
  const compact = ['badge', 'numeric', 'date'].includes(column.type)
  if (
    compact &&
    column.width == null &&
    !Object.prototype.hasOwnProperty.call(saved, column.key) &&
    floor > 0
  )
    return renderedWidth(
      column,
      { ...view, widths: { ...saved, [column.key]: Math.max(floor, natural) } },
      floor
    )
  return renderedWidth(column, view, floor)
}

/** A column that may take the remaining width: resizable, unlocked, and not fixed (its min equals its max). */
const canFill = column => {
  if (!isResizable(column) || column.lock) return false
  const size = column.size || {}
  return !(size.max != null && size.max === size.min)
}

/**
 * One flexible data column fills the region; locked columns and controls keep their widths. A text or link
 * column is preferred; a table without an eligible one (its text column locked to the start, say) fills with
 * its last eligible column of any type, so it still reaches the region's edge.
 */
export const fillColumn = table => {
  const columns = table.layout.columns.filter(canFill).reverse()
  return (
    columns.find(column => ['text', 'link'].includes(column.type)) || columns[0]
  )
}

const densityOf = ({ config, state }) =>
  (state && state.view && state.view.density) ||
  (config && config.defaultDensity) ||
  DEFAULT_DENSITY

/**
 * The rendered columns, in header order, each with its width before spare width; key is null for a control
 * column. A control column's width comes from its control's size (controlSize.js), so its focus ring fits
 * inside the cell.
 * @param {import('../types').TableContextValue} table
 * @returns {Array<{key: ?string, width: number}>}
 */
export const baseWidths = table => {
  const { config, layout } = table
  const density = densityOf(table)
  const out = []
  if (layout.selectable)
    out.push({ key: null, width: controlWidth('select', density) })
  if (layout.expandable)
    out.push({ key: null, width: controlWidth('expand', density) })
  layout.columns.forEach(column => {
    const width = columnWidth(table, column)
    out.push({
      key: column.key,
      width: typeof width === 'number' ? width : DEFAULT_WIDTHS.text,
    })
  })
  if (layout.hasActions)
    out.push({
      key: null,
      width: controlWidth('actions', density, {
        labelled: isText(config.rowActionsLabel),
      }),
    })
  return out
}

/** The column widths are authoritative, including when minTableWidth exceeds their allowed total. */
export const tableMinWidth = table =>
  baseWidths(table).reduce((total, col) => total + col.width, 0)

/**
 * Each data column's share of the spare width, keyed by column key: the region's content width (or the
 * configured minimum table width, when larger) less the widths before spare width. Empty until the region is
 * measured, and while the table is as wide as its region or wider.
 * @param {import('../types').TableContextValue} table
 * @returns {Object<string, number>}
 */
export const spareOf = table => {
  const geometry = table.geometry || {}
  const content = geometry.contentWidth || 0
  if (!(content > 0)) return {}
  const base = baseWidths(table)
  const sum = base.reduce((total, col) => total + col.width, 0)
  const spare = Math.max(content, table.config.minTableWidth || 0, sum) - sum
  if (!(spare >= 1)) return {}
  const naturals = geometry.naturals || {}
  const saved = (table.state.view && table.state.view.widths) || {}
  const byKey = new Map(
    table.layout.columns.map(column => [column.key, column])
  )
  const allocated = spareWidths(
    base
      .filter(
        col =>
          col.key !== null &&
          isResizable(byKey.get(col.key)) &&
          !['badge', 'numeric', 'date'].includes(byKey.get(col.key).type)
      )
      .map(col => ({
        key: col.key,
        width: col.width,
        natural: naturals[col.key],
        max: (byKey.get(col.key).size || {}).max,
        saved: Object.prototype.hasOwnProperty.call(saved, col.key),
      })),
    spare
  )
  const fill = fillColumn(table)
  if (fill) {
    const remaining =
      spare - Object.values(allocated).reduce((sum, width) => sum + width, 0)
    if (remaining > 0)
      allocated[fill.key] = (allocated[fill.key] || 0) + remaining
  }
  return allocated
}

/**
 * The rendered columns, in header order, each with the width it renders at: its width before spare width
 * plus its share of the spare width; key is null for a control column.
 * @param {import('../types').TableContextValue} table
 * @returns {Array<{key: ?string, width: number, spare: number}>}
 */
export const columnWidths = table => {
  const spare = spareOf(table)
  return baseWidths(table).map(col => {
    const extra = (col.key !== null && spare[col.key]) || 0
    return { ...col, width: col.width + extra, spare: extra }
  })
}

/** Exact table width: the browser must not redistribute unallocated space across bounded columns. */
export const tableWidth = table =>
  columnWidths(table).reduce((total, col) => total + col.width, 0)

/** The width a data column renders at, its share of the spare width included. */
export const shownWidth = (table, column) => {
  const width = columnWidth(table, column)
  if (typeof width !== 'number') return width
  return width + (spareOf(table)[column.key] || 0)
}

/** A resize preserves other auto-sized columns except the fill column, which takes the remaining space. */
export const widthsForResize = (table, column, width) => {
  const view = table.state.view || {}
  const widths = { ...view.widths }
  const naturals = (table.geometry && table.geometry.naturals) || {}
  const spare = spareOf(table)
  const fill = fillColumn(table)
  table.layout.columns.forEach(item => {
    const base = columnWidth(table, item)
    if (item !== fill && isResizable(item) && naturals[item.key] > base)
      widths[item.key] = base + (spare[item.key] || 0)
  })
  return resizedWidths({ ...view, widths }, column, width)
}

/** A fill column cannot shrink below the space remaining after the other columns. */
export const resizeFloor = (table, column) => {
  const floor = floorOf(table, column)
  if (fillColumn(table) !== column) return floor
  const others = columnWidths(table)
    .filter(item => item.key !== column.key)
    .reduce((sum, item) => sum + item.width, 0)
  const target = Math.max(
    (table.geometry || {}).contentWidth || 0,
    table.config.minTableWidth || 0
  )
  return Math.max(floor || 0, target - others)
}

/** The same allocation is used for a live preview and a committed resize. */
export const previewWidths = (table, column, width) =>
  width === null
    ? columnWidths(table)
    : columnWidths({
        ...table,
        state: {
          ...table.state,
          view: {
            ...table.state.view,
            widths: widthsForResize(table, column, width),
          },
        },
      })

export const ColGroup = () => {
  const table = useTable()
  // the colgroup renders before every header and body cell, so the lock plan they read is this render's
  const { geometry, layout } = table
  const widths = columnWidths(table)
  if (geometry && typeof geometry.syncColumns === 'function')
    geometry.syncColumns(layout.columns, widths)
  return (
    <colgroup>
      {widths.map((col, index) =>
        col.key === null ? (
          <col
            key={`control-${index}`}
            className="me-table-col-control"
            style={{ width: `${col.width}px` }}
          />
        ) : (
          <col
            key={col.key}
            data-column-key={col.key}
            style={{ width: `${col.width}px` }}
          />
        )
      )}
    </colgroup>
  )
}
